import {
  PROMOTION_ANIMATIONS,
  PROMOTION_LABEL_EFFECTS,
  PROMOTION_OFFSET_CLAMP,
  PROMOTION_PLACEMENTS,
  PROMOTION_SIZES,
  PROMOTION_TYPES,
  PROMOTION_VARIANTS,
  PROMOTION_STATUS_VARIANTS,
  type PromotionAnimation,
  type PromotionBadgeOffset,
  type PromotionEntry,
  type PromotionLabelEffect,
  type PromotionPlacement,
  type PromotionSize,
  type PromotionType,
  type PromotionVariant,
} from '@common/types/promotions';
import { ESearchPredicateTypes } from '@common/definitions';
import { normalizeTailwindClasses } from './normalize-tailwind-classes';

const VALID_TYPES = new Set<string>(Object.values(PROMOTION_TYPES));
const VALID_PROMO_VARIANTS = new Set<string>(Object.values(PROMOTION_VARIANTS));
const VALID_STATUS_VARIANTS = new Set<string>(Object.values(PROMOTION_STATUS_VARIANTS));
const VALID_PLACEMENTS = new Set<string>(Object.values(PROMOTION_PLACEMENTS));
const VALID_ANIMATIONS = new Set<string>(Object.values(PROMOTION_ANIMATIONS));
const VALID_LABEL_EFFECTS = new Set<string>(Object.values(PROMOTION_LABEL_EFFECTS));
const VALID_SIZES = new Set<string>(Object.values(PROMOTION_SIZES));
const VALID_PREDICATE_TYPES = new Set<string>(Object.values(ESearchPredicateTypes));

function readString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === 'number' && !Number.isNaN(value) ? value : undefined;
}

function readStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;

  return value.filter((item): item is string => typeof item === 'string');
}

function normalizeType(value: unknown): PromotionType | null {
  const type = readString(value);
  return type && VALID_TYPES.has(type) ? type as PromotionType : null;
}

function normalizeVariant(value: unknown, type: PromotionType): PromotionVariant | null {
  const variant = readString(value);
  if (!variant) return null;
  if (VALID_PROMO_VARIANTS.has(variant)) return variant as PromotionVariant;
  if (type === PROMOTION_TYPES.BADGE && VALID_STATUS_VARIANTS.has(variant)) {
    return variant as PromotionVariant;
  }
  return null;
}

function normalizePlacement(value: unknown): PromotionPlacement | undefined {
  const placement = readString(value);
  return placement && VALID_PLACEMENTS.has(placement)
    ? placement as PromotionPlacement
    : undefined;
}

function clampOffsetAxis(value: number): number {
  return Math.max(-PROMOTION_OFFSET_CLAMP, Math.min(PROMOTION_OFFSET_CLAMP, value));
}

function normalizeOffset(value: unknown): PromotionBadgeOffset | undefined {
  if (!value || typeof value !== 'object') return undefined;

  const offsetObj = value as Record<string, unknown>;
  const x = readNumber(offsetObj.x);
  const y = readNumber(offsetObj.y);

  if (x === undefined && y === undefined) return undefined;

  return {
    ...(x !== undefined ? { x: clampOffsetAxis(x) } : {}),
    ...(y !== undefined ? { y: clampOffsetAxis(y) } : {}),
  };
}

function normalizeAnimation(value: unknown): PromotionAnimation | undefined {
  const animation = readString(value);
  return animation && VALID_ANIMATIONS.has(animation)
    ? animation as PromotionAnimation
    : undefined;
}

function normalizeLabelEffect(value: unknown): PromotionLabelEffect | undefined {
  const labelEffect = readString(value);
  return labelEffect && VALID_LABEL_EFFECTS.has(labelEffect)
    ? labelEffect as PromotionLabelEffect
    : undefined;
}

function normalizeSize(value: unknown): PromotionSize | undefined {
  const size = readString(value);
  return size && VALID_SIZES.has(size) ? size as PromotionSize : undefined;
}

function normalizeAudience(raw: Record<string, unknown>) {
  const audienceRaw = raw.audience;
  if (!audienceRaw || typeof audienceRaw !== 'object') return undefined;

  const audienceObj = audienceRaw as Record<string, unknown>;
  const predicateType = readString(audienceObj.predicate_type ?? audienceObj.predicateType);
  const { predicates } = audienceObj;

  if (!predicateType || !VALID_PREDICATE_TYPES.has(predicateType)) return undefined;
  if (!Array.isArray(predicates)) return undefined;

  return {
    predicate_type: predicateType as ESearchPredicateTypes,
    predicates,
  };
}

function normalizePromotionEntry(raw: unknown): PromotionEntry | null {
  if (!raw || typeof raw !== 'object') return null;

  const entry = raw as Record<string, unknown>;
  const key = readString(entry.key);
  const type = normalizeType(entry.type);
  const variant = type ? normalizeVariant(entry.variant, type) : null;
  const enabled = readBoolean(entry.enabled);
  const startAt = readString(entry.startAt ?? entry.start_at);
  const endAt = readString(entry.endAt ?? entry.end_at);

  if (!key || !type || !variant || enabled === undefined || !startAt || !endAt) {
    return null;
  }

  const ctaRaw = entry.cta;
  let cta: PromotionEntry['cta'];

  if (ctaRaw && typeof ctaRaw === 'object') {
    const ctaObj = ctaRaw as Record<string, unknown>;
    const label = readString(ctaObj.label);
    const href = readString(ctaObj.href);

    if (label && href) {
      cta = {
        label,
        href,
        external: readBoolean(ctaObj.external),
      };
    }
  }

  return {
    key,
    type,
    label: readString(entry.label),
    message: readString(entry.message),
    cta,
    variant,
    enabled,
    startAt,
    endAt,
    dismissible: readBoolean(entry.dismissible),
    priority: readNumber(entry.priority),
    routes: readStringArray(entry.routes),
    excludedRoutes: readStringArray(entry.excludedRoutes ?? entry.excluded_routes),
    audience: normalizeAudience(entry),
    placement: type === 'badge' ? normalizePlacement(entry.placement) : undefined,
    offset: type === 'badge' ? normalizeOffset(entry.offset) : undefined,
    animation: type === 'badge' ? normalizeAnimation(entry.animation) : undefined,
    labelEffect: type === 'badge' ? normalizeLabelEffect(entry.labelEffect ?? entry.label_effect) : undefined,
    size: type === 'badge' ? normalizeSize(entry.size) : undefined,
    className: type === 'badge' ? normalizeTailwindClasses(entry.className ?? entry.class_name) : undefined,
    chipClassName: type === 'badge' ? normalizeTailwindClasses(entry.chipClassName ?? entry.chip_class_name) : undefined,
    labelClassName: type === 'badge' ? normalizeTailwindClasses(entry.labelClassName ?? entry.label_class_name) : undefined,
  };
}

export function parsePromotionsResponse(data: unknown): PromotionEntry[] {
  if (!Array.isArray(data)) return [];

  return data
    .map(normalizePromotionEntry)
    .filter((entry): entry is PromotionEntry => entry !== null);
}
